import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress,
  Avatar,
} from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import UploadIcon from '@mui/icons-material/Upload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { ImageUploadService } from '../../accounting/inventory/services/imageUploadService';
import { getGeneralConfig, saveGeneralConfig } from '../../../services/ecommerceApi';

interface FaviconSettingsProps {
  companyId: string | null;
}

const MAX_FAVICON_SIZE = 1 * 1024 * 1024; // 1MB

const FaviconSettings: React.FC<FaviconSettingsProps> = ({ companyId }) => {
  const [currentFaviconUrl, setCurrentFaviconUrl] = useState<string | null>(null);
  const [faviconFile, setFaviconFile] = useState<File | null>(null);
  const [faviconPreview, setFaviconPreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await getGeneralConfig(companyId);
        if (!cancelled && response?.data?.faviconUrl) {
          setCurrentFaviconUrl(response.data.faviconUrl);
        }
      } catch (err) {
        console.error('❌ [FAVICON] Mevcut favicon getirilemedi:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    setSuccess(null);
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Lütfen bir görsel dosyası seçin (PNG, JPG, ICO, SVG).');
      return;
    }
    if (file.size > MAX_FAVICON_SIZE) {
      setError("Favicon boyutu 1MB'dan büyük olamaz.");
      return;
    }

    setFaviconFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setFaviconPreview(reader.result as string);
    reader.readAsDataURL(file);
  }, []);

  const handleUpload = useCallback(async () => {
    if (!faviconFile || !companyId) {
      setError('Lütfen önce bir favicon dosyası seçin.');
      return;
    }

    setIsUploading(true);
    setError(null);
    setSuccess(null);

    try {
      // 1. Image Service'e yükle (e-ticaret görselleri için standart servis)
      const uploaded = await ImageUploadService.uploadImages([faviconFile]);
      const downloadURL = uploaded?.[0]?.originalUrl;

      if (!downloadURL) {
        throw new Error('Görsel servisi geçerli bir URL döndürmedi.');
      }

      // 2. SQL'e kaydet — mağaza sitesi (storefront) bu URL'i favicon olarak kullanır
      const response = await saveGeneralConfig({
        companyId,
        faviconUrl: downloadURL,
      });

      if (response?.success !== false) {
        setCurrentFaviconUrl(downloadURL);
        setFaviconFile(null);
        setFaviconPreview(null);
        setSuccess('Favicon kaydedildi. Mağaza sitesi sekmesinde bu ikon görünecek.');
      } else {
        setError('Favicon kaydedilemedi. Lütfen tekrar deneyin.');
      }
    } catch (err: any) {
      console.error('❌ [FAVICON] Yükleme hatası:', err);
      setError(err?.message || 'Favicon yüklenirken bir hata oluştu.');
    } finally {
      setIsUploading(false);
    }
  }, [faviconFile, companyId]);

  const handleDelete = useCallback(async () => {
    if (!companyId) return;

    // Sadece seçili (henüz kaydedilmemiş) dosya varsa, kaydı değiştirmeden temizle
    if (faviconFile && !currentFaviconUrl) {
      setFaviconFile(null);
      setFaviconPreview(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setIsDeleting(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await saveGeneralConfig({
        companyId,
        faviconUrl: '',
      });

      if (response?.success !== false) {
        setCurrentFaviconUrl(null);
        setFaviconFile(null);
        setFaviconPreview(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        setSuccess('Favicon kaldırıldı. Mağaza sitesi varsayılan ikonu kullanacak.');
      } else {
        setError('Favicon kaldırılamadı. Lütfen tekrar deneyin.');
      }
    } catch (err: any) {
      console.error('❌ [FAVICON] Silme hatası:', err);
      setError(err?.message || 'Favicon kaldırılırken bir hata oluştu.');
    } finally {
      setIsDeleting(false);
    }
  }, [companyId, faviconFile, currentFaviconUrl]);

  const previewSrc = faviconPreview || currentFaviconUrl || undefined;
  const hasFavicon = Boolean(faviconFile || currentFaviconUrl);
  const busy = isUploading || isDeleting;

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <ImageIcon color="primary" sx={{ mr: 1.5 }} />
        <Typography variant="h6">Site İkonu (Favicon)</Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Mağaza sitenizin tarayıcı sekmesinde ve yer imlerinde görünecek ikonu yükleyin.
        En iyi sonuç için kare (örn. 32x32 veya 64x64) PNG/ICO/SVG kullanın. En fazla 1MB.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Avatar
          src={previewSrc}
          variant="rounded"
          sx={{ width: 64, height: 64, bgcolor: '#f5f5f5', border: '1px solid #e0e0e0' }}
        >
          <ImageIcon color="disabled" />
        </Avatar>

        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/x-icon,image/vnd.microsoft.icon,image/svg+xml"
            hidden
            onChange={handleFileChange}
          />
          <Button
            variant="outlined"
            startIcon={<ImageIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={busy}
          >
            Dosya Seç
          </Button>
          <Button
            variant="contained"
            startIcon={isUploading ? <CircularProgress size={18} color="inherit" /> : <UploadIcon />}
            onClick={handleUpload}
            disabled={!faviconFile || busy}
          >
            {isUploading ? 'Yükleniyor...' : 'Yükle ve Kaydet'}
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={isDeleting ? <CircularProgress size={18} color="inherit" /> : <DeleteOutlineIcon />}
            onClick={handleDelete}
            disabled={!hasFavicon || busy}
          >
            {isDeleting ? 'Kaldırılıyor...' : 'Sil'}
          </Button>
        </Box>
      </Box>

      {faviconFile && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          Seçilen dosya: {faviconFile.name}
        </Typography>
      )}
    </Paper>
  );
};

export default FaviconSettings;
